import React, { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, TrendingUp, Users } from 'lucide-react';
import { homeData } from '../data/mock';

/* ── Floating + 3D Tilt Profile Image (fundo transparente) ── */
const AnchorImage = ({ src }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const rX = ((y - cy) / cy) * -9;
      const rY = ((x - cx) / cx) * 9;
      el.style.transform = `perspective(700px) rotateX(${rX}deg) rotateY(${rY}deg) scale(1.03)`;
    };
    const onLeave = () => {
      el.style.transform = 'perspective(700px) rotateX(0deg) rotateY(0deg) scale(1)';
    };

    el.addEventListener('mousemove', onMove);
    el.addEventListener('mouseleave', onLeave);
    return () => {
      el.removeEventListener('mousemove', onMove);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      style={{
        display: 'inline-block',
        transition: 'transform 0.14s ease-out',
        cursor: 'default',
        transformStyle: 'preserve-3d',
      }}
    >
      {/* Sem Polaroid — imagem colada diretamente com fundo transparente */}
      <div
        className="floating-polaroid"
        style={{
          background: 'transparent',
          position: 'relative',
          maxWidth: '380px',
        }}
      >
        <img
          src={src}
          alt="Samara Morgana — Filmmaker"
          style={{
            width: '100%',
            display: 'block',
            background: 'transparent',
          }}
        />
      </div>
    </div>
  );
};

/* ── About / Anchor Section — primeira dobra da Home ── */
const AboutSection = () => (
  <section
    style={{
      backgroundColor: '#f7f5ef',
      padding: 'clamp(4rem, 8vw, 6.5rem) clamp(1.5rem, 5vw, 4.5rem) clamp(3rem, 6vw, 5rem)',
      position: 'relative',
      overflow: 'hidden',
    }}
  >
    {/* Glow sutil decorativo */}
    <div
      style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: '45%',
        height: '100%',
        background: 'radial-gradient(ellipse at 80% 40%, rgba(210,190,150,0.07) 0%, transparent 65%)',
        pointerEvents: 'none',
      }}
    />

    <div
      className="about-grid"
      style={{
        maxWidth: '1100px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: 'auto 1fr',
        gap: 'clamp(2rem, 6vw, 6rem)',
        alignItems: 'center',
      }}
    >
      {/* Imagem âncora */}
      <div style={{ flexShrink: 0 }}>
        <AnchorImage src={homeData.profileImage} />
      </div>

      {/* Texto lado direito */}
      <div>
        {/* Washi tape decorativo */}
        <div
          style={{
            width: '80px',
            height: '14px',
            background: 'rgba(210,190,130,0.40)',
            borderRadius: '2px',
            marginBottom: '1.6rem',
            transform: 'rotate(-0.5deg)',
          }}
        />
        <p
          style={{
            fontFamily: '"Nunito", sans-serif',
            fontSize: '9.5px',
            letterSpacing: '0.3em',
            color: '#a09070',
            margin: '0 0 0.9rem 0',
            textTransform: 'uppercase',
          }}
        >
          SOBRE
        </p>
        <h2
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontSize: 'clamp(1.7rem, 3.2vw, 2.7rem)',
            color: '#1a1a1a',
            margin: '0 0 1.4rem 0',
            fontWeight: 600,
            lineHeight: 1.2,
          }}
        >
          Narrativas que conectam<br />marcas a pessoas.
        </h2>
        <p
          style={{
            fontFamily: '"Nunito", sans-serif',
            fontSize: 'clamp(14px, 1.4vw, 15.5px)',
            color: '#4a4a4a',
            lineHeight: 1.88,
            maxWidth: '560px',
          }}
        >
          {homeData.about}
        </p>
        <a
          href="https://wa.me/5537984162121"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-block',
            marginTop: '2rem',
            padding: '12px 30px',
            background: '#1a1a1a',
            color: '#f7f5ef',
            fontFamily: '"Nunito", sans-serif',
            fontSize: '10.5px',
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            textDecoration: 'none',
            borderRadius: '2px',
            transition: 'background 0.25s ease, transform 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#333'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = '#1a1a1a'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
          Entrar em Contato
        </a>
      </div>
    </div>
  </section>
);

/* ── Decision Cards Section ── */
const CARD_ROTATIONS = [-1.2, 0.8, -0.6];
const CARD_TAPE_POS = ['top-left', 'top-center', 'top-right'];
const CARD_TAPE_COLORS = [
  'rgba(210,190,130,0.45)',
  'rgba(170,205,185,0.42)',
  'rgba(205,175,175,0.42)',
];
const cardIcons = [Camera, TrendingUp, Users];

const DecisionCard = ({ card, idx }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = cardIcons[idx];
  const rotation = CARD_ROTATIONS[idx];
  const tapePos = CARD_TAPE_POS[idx];
  const tapeColor = CARD_TAPE_COLORS[idx];

  const getTape = () => {
    const base = {
      position: 'absolute',
      width: '76px',
      height: '17px',
      background: tapeColor,
      borderRadius: '2px',
      zIndex: 3,
      pointerEvents: 'none',
    };
    if (tapePos === 'top-left') return { ...base, top: '-8px', left: '18px', transform: 'rotate(-2.5deg)' };
    if (tapePos === 'top-right') return { ...base, top: '-8px', right: '18px', transform: 'rotate(2.5deg)' };
    return { ...base, top: '-8px', left: '50%', transform: 'translateX(-50%) rotate(-0.6deg)' };
  };

  return (
    <Link
      to={card.link}
      style={{ textDecoration: 'none' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        style={{
          background: '#fff',
          padding: '10px 10px 24px 10px',
          boxShadow: hovered
            ? '0 18px 50px rgba(0,0,0,0.16), 0 4px 14px rgba(0,0,0,0.08)'
            : '0 6px 26px rgba(0,0,0,0.10), 0 1px 6px rgba(0,0,0,0.05)',
          transform: `rotate(${hovered ? rotation * 0.25 : rotation}deg) translateY(${hovered ? -10 : 0}px)`,
          transition: 'box-shadow 0.3s ease, transform 0.3s ease',
          position: 'relative',
          borderRadius: '2px',
          height: '100%',
        }}
      >
        <div style={getTape()} />

        {/* Imagem */}
        <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', position: 'relative', background: '#111' }}>
          <img
            src={card.thumbnail}
            alt={card.title}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: hovered ? 'scale(1.06)' : 'scale(1)',
              transition: 'transform 0.45s ease',
              filter: hovered ? 'brightness(0.72)' : 'brightness(0.88)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 55%)',
            }}
          />
          <div
            style={{
              position: 'absolute',
              bottom: '14px',
              left: '14px',
              background: 'rgba(255,255,255,0.14)',
              borderRadius: '50%',
              padding: '9px',
              backdropFilter: 'blur(4px)',
            }}
          >
            <Icon size={19} color="#f7f5ef" />
          </div>
        </div>

        {/* Texto */}
        <div style={{ padding: '16px 8px 4px' }}>
          <h3
            style={{
              fontFamily: '"Cormorant Garamond", "Georgia", serif',
              fontSize: '1.15rem',
              color: '#1a1a1a',
              margin: '0 0 4px',
              fontWeight: 600,
              lineHeight: 1.25,
            }}
          >
            {card.title}
          </h3>
          <p
            style={{
              fontFamily: '"Nunito", sans-serif',
              fontSize: '8.5px',
              letterSpacing: '0.2em',
              color: '#a09070',
              margin: '0 0 10px',
              textTransform: 'uppercase',
            }}
          >
            {card.subtitle}
          </p>
          <p
            style={{
              fontFamily: '"Nunito", sans-serif',
              fontSize: '13px',
              color: '#5a5a5a',
              lineHeight: 1.65,
              margin: '0 0 14px',
            }}
          >
            {card.description}
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontFamily: '"Nunito", sans-serif',
              fontSize: '10.5px',
              color: '#1a1a1a',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
            }}
          >
            VER PROJETOS <ArrowRight size={13} />
          </div>
        </div>
      </div>
    </Link>
  );
};

const DecisionSection = () => (
  <section
    style={{
      backgroundColor: '#ece9e2',
      padding: 'clamp(4rem, 8vw, 7rem) clamp(1.5rem, 4vw, 4rem)',
      borderTop: '1px solid rgba(0,0,0,0.04)',
    }}
  >
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
        <p
          style={{
            fontFamily: '"Nunito", sans-serif',
            fontSize: '9.5px',
            letterSpacing: '0.3em',
            color: '#a09070',
            textTransform: 'uppercase',
            margin: '0 0 0.7rem',
          }}
        >
          PORTFÓLIO
        </p>
        <h2
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontSize: 'clamp(1.9rem, 3.8vw, 3rem)',
            color: '#1a1a1a',
            margin: 0,
            fontWeight: 600,
          }}
        >
          Escolha um universo
        </h2>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '2.5rem',
        }}
      >
        {homeData.decisionCards.map((card, i) => (
          <DecisionCard key={card.id} card={card} idx={i} />
        ))}
      </div>
    </div>
  </section>
);

/* ── Página Principal ── */
const Home = () => (
  <div style={{ backgroundColor: '#f7f5ef' }}>
    <AboutSection />
    <DecisionSection />
  </div>
);

export default Home;
